import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
// @ts-ignore - JS module, no types
import UserService from "../../../services/UserService";
import { rolesAtOrAbove } from "../../../utils/common/productRoles";
import { mapUrlToProductId } from "../../../utils/common/mapUrlToProduct";
import { Button } from "../../../ui/button";
import { Switch } from "../../../ui/switch";
import { Input } from "../../../ui/input";
import { Textarea } from "../../../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  createGuardrail,
  deleteGuardrail,
  fetchGuardrails,
  fetchHistory,
  fetchSchema,
  setGuardrailActive,
  updateGuardrail,
  type Condition,
  type ControllerSchema,
  type FieldType,
  type Guardrail,
  type GuardrailInput,
  type HistoryEntry,
  type Segment,
} from "../lib/controllerApi";

// ── Athena design tokens ────────────────────────────────────────────────────
const EYEBROW = "text-[11px] font-medium uppercase tracking-[0.16em] text-amber";
const MICRO_LABEL = "text-[11px] font-medium uppercase tracking-[0.14em] text-amber";
const TH = "px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground";
// Single clean border on focus — drop the base component's offset focus-ring
// (which read as a second, boxed border) and just turn the border amber.
const FIELD_CLS =
  "bg-[hsl(216,11%,13%)] border-[hsl(216,11%,26%)] text-foreground " +
  "focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 " +
  "focus:border-amber focus-visible:border-amber";
const HAIRLINE = "hsla(216, 11%, 45%, 0.28)";

// One consistent badge style for every segment (PG / CORP / ALL).
const SEGMENT_BADGE_CLS = "border-border bg-muted/40 text-foreground/80";

// Minimal fallback so the builder still works if GET /controller/schema hasn't
// resolved. The live schema (full catalog) overrides this once loaded.
const FALLBACK_SCHEMA: ControllerSchema = {
  segments: ["ALL", "PG", "CORP"],
  sections: ["Deal", "Bureau", "PG", "Corp"],
  fields: [
    { key: "loanAmount", label: "Loan Amount", section: "Deal", type: "number" },
    { key: "pgFicoScore", label: "FICO Score", section: "PG", type: "number" },
    { key: "paydex", label: "Paydex", section: "Bureau", type: "number" },
    { key: "pnMasterScore", label: "PN Master Score", section: "Bureau", type: "number" },
    { key: "timeInBusiness", label: "Time In Business", section: "Bureau", type: "number" },
  ],
  operators: {
    lt: { symbol: "<", label: "less than" },
    lte: { symbol: "≤", label: "less than or equal to" },
    gt: { symbol: ">", label: "greater than" },
    gte: { symbol: "≥", label: "greater than or equal to" },
    eq: { symbol: "=", label: "equal to" },
    ne: { symbol: "≠", label: "not equal to" },
    is_true: { symbol: "is true", label: "is on / true" },
    is_false: { symbol: "is false", label: "is off / false" },
    is: { symbol: "is", label: "is" },
    is_not: { symbol: "is not", label: "is not" },
    is_one_of: { symbol: "is one of", label: "is one of" },
  },
  operators_by_type: {
    number: ["lt", "lte", "gt", "gte", "eq", "ne"],
    boolean: ["is_true", "is_false"],
    text: ["is", "is_not", "is_one_of"],
  },
};

function SegmentBadge({ segment }: { segment: Segment }) {
  return (
    <span
      className={`inline-flex items-center border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] ${SEGMENT_BADGE_CLS}`}
    >
      {segment}
    </span>
  );
}

function AccessDenied() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center space-y-3">
        <div className={EYEBROW}>Restricted</div>
        <h2 className="text-lg font-semibold text-amber">Access Denied</h2>
        <p className="text-sm text-muted-foreground">
          The Guardrails Controller is restricted to Contributors and above.
        </p>
        <Link to="/underwriting-engine" className="inline-block text-sm text-amber hover:underline">
          ← Back to Underwriting Engine
        </Link>
      </div>
    </div>
  );
}

const defaultValueForType = (t: FieldType): Condition["value"] =>
  t === "number" ? 0 : t === "boolean" ? null : "";

export default function Controller() {
  const ueProductId = mapUrlToProductId("underwriting-engine") as number;
  const canAccessController: boolean = UserService.hasRole(
    rolesAtOrAbove(ueProductId, "contributor")
  );

  const [schema, setSchema] = useState<ControllerSchema | null>(null);
  const [guardrails, setGuardrails] = useState<Guardrail[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Guardrail | null>(null);
  const [draft, setDraft] = useState<GuardrailInput | null>(null);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<Guardrail | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTitle, setHistoryTitle] = useState("");
  const [history, setHistory] = useState<HistoryEntry[]>([]);

  // Live schema when loaded, else the fallback — so the builder is never empty.
  const sch = schema ?? FALLBACK_SCHEMA;

  const fieldLabel = useMemo(() => {
    const m = new Map(sch.fields.map((f) => [f.key, f.label]));
    return (key: string) => m.get(key) ?? key;
  }, [sch]);

  const typeOf = useMemo(() => {
    const m = new Map(sch.fields.map((f) => [f.key, f.type]));
    return (key: string): FieldType => m.get(key) ?? "number";
  }, [sch]);

  const firstOpForType = (t: FieldType) => sch.operators_by_type[t]?.[0] ?? "eq";

  // One condition rendered for humans, per its field type.
  function conditionText(c: Condition): string {
    const label = fieldLabel(c.field);
    const t = typeOf(c.field);
    const op = sch.operators[c.op];
    if (t === "boolean") return `${label} ${op?.label ?? c.op}`;
    if (t === "text") {
      const v = Array.isArray(c.value) ? c.value.join(", ") : String(c.value ?? "");
      return c.op === "is_one_of"
        ? `${label} is one of [${v}]`
        : `${label} ${op?.symbol ?? c.op} "${v}"`;
    }
    return `${label} ${op?.symbol ?? c.op} ${c.value}`;
  }

  function newCondition(): Condition {
    const f = sch.fields[0];
    const t = f?.type ?? "number";
    return { field: f?.key ?? "", op: firstOpForType(t), value: defaultValueForType(t) };
  }

  const counts = useMemo(() => {
    const active = guardrails.filter((g) => g.is_active).length;
    const bySeg = { ALL: 0, PG: 0, CORP: 0 } as Record<Segment, number>;
    guardrails.forEach((g) => (bySeg[g.segment] += 1));
    return { total: guardrails.length, active, bySeg };
  }, [guardrails]);

  async function load() {
    setLoading(true);
    setLoadError(null);
    try {
      setGuardrails(await fetchGuardrails());
    } catch (e: any) {
      const msg = e?.response?.data?.detail || "Failed to load guardrails";
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!canAccessController) return;
    // Schema is static (no DB) — load it on its own so the rule builder stays
    // usable even when the guardrails table read fails (e.g. migration pending).
    fetchSchema().then(setSchema).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessController]);

  if (!canAccessController) return <AccessDenied />;

  function openAdd() {
    setEditing(null);
    setDraft({
      name: "",
      description: "",
      segment: "PG",
      conditions: [newCondition()],
      na_reason: "",
      sort_order: 100,
    });
    setFormOpen(true);
  }

  function openEdit(g: Guardrail) {
    setEditing(g);
    setDraft({
      name: g.name,
      description: g.description ?? "",
      segment: g.segment,
      // "is one of" stores an array; show it as a comma string in the text input.
      conditions: g.conditions.map((c) => ({
        ...c,
        value: Array.isArray(c.value) ? c.value.join(", ") : c.value,
      })),
      na_reason: g.na_reason,
      sort_order: g.sort_order,
    });
    setFormOpen(true);
  }

  async function toggleActive(g: Guardrail, next: boolean) {
    try {
      await setGuardrailActive(g.guardrail_id, next);
      setGuardrails((prev) =>
        prev.map((x) => (x.guardrail_id === g.guardrail_id ? { ...x, is_active: next } : x)),
      );
      toast.success(`${g.name} ${next ? "enabled" : "disabled"}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  }

  async function save() {
    if (!draft) return;
    if (!draft.name.trim() || !draft.na_reason.trim()) {
      toast.error("Name and NA reason are required.");
      return;
    }
    if (!draft.conditions.length || draft.conditions.some((c) => !c.field)) {
      toast.error("Each condition needs a field.");
      return;
    }
    // Shape each value by its field type: number -> Number, boolean -> null (the
    // operator carries the truth), text -> string (backend splits "is one of").
    const payload: GuardrailInput = {
      ...draft,
      conditions: draft.conditions.map((c) => {
        const t = typeOf(c.field);
        if (t === "number") return { field: c.field, op: c.op, value: Number(c.value) };
        if (t === "boolean") return { field: c.field, op: c.op, value: null };
        return { field: c.field, op: c.op, value: String(c.value ?? "") };
      }),
      sort_order: Number(draft.sort_order),
    };
    setSaving(true);
    try {
      if (editing) {
        await updateGuardrail(editing.guardrail_id, payload);
        toast.success("Guardrail updated");
      } else {
        await createGuardrail(payload);
        toast.success("Guardrail created");
      }
      setFormOpen(false);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteGuardrail(deleteTarget.guardrail_id);
      toast.success("Guardrail deleted");
      setDeleteTarget(null);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  }

  async function openHistory(g?: Guardrail) {
    setHistoryTitle(g ? g.name : "All guardrails");
    setHistoryOpen(true);
    try {
      setHistory(await fetchHistory(g?.guardrail_id));
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Failed to load history");
      setHistory([]);
    }
  }

  // ── Draft condition helpers ──
  function updateCondition(i: number, patch: Partial<Condition>) {
    setDraft((d) =>
      d ? { ...d, conditions: d.conditions.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) } : d,
    );
  }
  // Changing the field resets the operator + value to match the new field's type.
  function setConditionField(i: number, key: string) {
    const t = typeOf(key);
    updateCondition(i, { field: key, op: firstOpForType(t), value: defaultValueForType(t) });
  }
  function addCondition() {
    setDraft((d) => (d ? { ...d, conditions: [...d.conditions, newCondition()] } : d));
  }
  function removeCondition(i: number) {
    setDraft((d) => (d ? { ...d, conditions: d.conditions.filter((_, idx) => idx !== i) } : d));
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-6xl px-6 py-10">
        {/* ── Header ── */}
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border/60 pb-6">
          <div className="min-w-0">
            <Link
              to="/underwriting-engine"
              className="text-xs text-muted-foreground transition-colors hover:text-amber"
            >
              ← Underwriting Engine
            </Link>
            <div className={`mt-3 ${EYEBROW}`}>Admin · Eligibility</div>
            <h1 className="mt-1 text-[26px] font-semibold leading-tight text-foreground">
              Guardrails Controller
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              Pre-model eligibility gates. A guardrail fires — the deal gets{" "}
              <span className="text-foreground/80">no Athena recommendation</span> — when{" "}
              <span className="text-foreground/80">all</span> its conditions are true. Changes go
              live immediately and are recorded in the audit history.
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button
              variant="outline"
              className="border-border bg-transparent text-foreground hover:border-amber hover:bg-transparent"
              onClick={() => openHistory()}
            >
              History
            </Button>
            <Button className="bg-amber font-medium text-background hover:bg-amber/90" onClick={openAdd}>
              + Add guardrail
            </Button>
          </div>
        </div>

        {/* ── Summary strip ── */}
        {!loading && !loadError && (
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
            <span>
              <span className="text-lg font-semibold tabular-nums text-foreground">{counts.total}</span>{" "}
              guardrails
            </span>
            <span>
              <span className="text-lg font-semibold tabular-nums text-foreground">{counts.active}</span> active
            </span>
            <span className="flex items-center gap-2">
              <SegmentBadge segment="ALL" /> {counts.bySeg.ALL}
              <SegmentBadge segment="PG" /> {counts.bySeg.PG}
              <SegmentBadge segment="CORP" /> {counts.bySeg.CORP}
            </span>
          </div>
        )}

        {/* ── Table ── */}
        <div className="mt-6 border border-border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead className="border-b border-border bg-background/40">
                <tr>
                  <th className={`${TH} w-16`}>On</th>
                  <th className={`${TH} w-24`}>Segment</th>
                  <th className={TH}>Guardrail</th>
                  <th className={TH}>Conditions</th>
                  <th className={TH}>NA reason</th>
                  <th className={`${TH} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-sm text-muted-foreground">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && guardrails.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-sm text-muted-foreground">
                      {loadError ? (
                        <div className="space-y-1.5">
                          <div className="font-medium text-destructive">Couldn't load guardrails</div>
                          <div className="text-xs">{loadError}</div>
                          <div className="text-xs">Please try again, or contact your administrator if it persists.</div>
                        </div>
                      ) : (
                        "No guardrails configured yet — add one to get started."
                      )}
                    </td>
                  </tr>
                )}
                {guardrails.map((g) => (
                  <tr
                    key={g.guardrail_id}
                    className={`border-b border-border/50 transition-colors last:border-0 hover:bg-muted/20 ${
                      g.is_active ? "" : "opacity-55"
                    }`}
                  >
                    <td className="px-4 py-3.5 align-top">
                      <Switch checked={g.is_active} onCheckedChange={(v) => toggleActive(g, v)} />
                    </td>
                    <td className="px-4 py-3.5 align-top">
                      <SegmentBadge segment={g.segment} />
                    </td>
                    <td className="px-4 py-3.5 align-top">
                      <div className="flex items-center gap-2 font-medium text-foreground">
                        {g.name}
                        {g.is_system && (
                          <span
                            className="border border-border px-1 py-0.5 text-[9px] uppercase tracking-wide text-muted-foreground"
                            title="Built-in rule — editable and can be disabled, but not deleted"
                          >
                            system
                          </span>
                        )}
                      </div>
                      {g.description && (
                        <div className="mt-1 max-w-xs text-xs leading-relaxed text-muted-foreground">
                          {g.description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3.5 align-top">
                      <div className="flex max-w-sm flex-wrap items-center gap-1.5">
                        {g.conditions.map((c, i) => (
                          <span key={i} className="flex items-center gap-1.5">
                            {i > 0 && (
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-amber/70">
                                and
                              </span>
                            )}
                            <span className="border border-border bg-background/60 px-1.5 py-0.5 font-mono text-[11px] text-foreground/85">
                              {conditionText(c)}
                            </span>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 align-top">
                      <span className="text-xs leading-relaxed text-foreground/75">{g.na_reason}</span>
                    </td>
                    <td className="px-4 py-3.5 align-top">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs text-muted-foreground hover:text-amber"
                          onClick={() => openEdit(g)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs text-muted-foreground hover:text-amber"
                          onClick={() => openHistory(g)}
                        >
                          History
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive disabled:opacity-30"
                          disabled={g.is_system}
                          title={g.is_system ? "System rules can't be deleted — disable instead" : "Delete"}
                          onClick={() => setDeleteTarget(g)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Add / Edit dialog ── */}
      <Dialog open={formOpen} onOpenChange={(v) => { if (!saving) setFormOpen(v); }}>
        <DialogContent className="max-w-2xl gap-0 overflow-hidden border-0 p-0">
          <div
            className="flex max-h-[86vh] flex-col"
            style={{ background: "hsl(218, 30%, 8%)", color: "hsl(30, 9%, 95%)" }}
          >
            {/* header */}
            <div className="shrink-0 px-8 pb-4 pt-7" style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
              <div className={EYEBROW}>{editing ? (editing.is_system ? "Edit · System rule" : "Edit guardrail") : "New guardrail"}</div>
              <DialogTitle className="mt-1 text-xl font-semibold text-foreground">
                {(draft?.name || "").trim() || (editing ? editing.name : "Add guardrail")}
              </DialogTitle>
              <DialogDescription className="mt-1 text-sm text-muted-foreground">
                Gates a deal to “no recommendation” when every condition below is true.
              </DialogDescription>
            </div>

            {/* body */}
            {draft && (
              <div className="flex-1 space-y-6 overflow-y-auto px-8 py-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 space-y-1.5">
                    <label className={MICRO_LABEL}>Name</label>
                    <Input
                      value={draft.name}
                      onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                      placeholder="e.g. PG FICO minimum"
                      className={FIELD_CLS}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className={MICRO_LABEL}>Segment</label>
                    <Select value={draft.segment} onValueChange={(v) => setDraft({ ...draft, segment: v as Segment })}>
                      <SelectTrigger className={FIELD_CLS}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {sch.segments.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s === "ALL" ? "ALL — every deal" : s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className={MICRO_LABEL}>Description <span className="text-muted-foreground/60 normal-case tracking-normal">(optional)</span></label>
                  <Input
                    value={draft.description ?? ""}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    placeholder="What this rule is for"
                    className={FIELD_CLS}
                  />
                </div>

                {/* Conditions */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className={MICRO_LABEL}>Conditions</label>
                    <span className="text-[11px] text-muted-foreground">all must be true to fire</span>
                  </div>
                  <div className="border border-[hsl(216,11%,24%)] bg-background/50">
                    {draft.conditions.map((c, i) => (
                      <div key={i}>
                        {i > 0 && (
                          <div className="flex items-center px-3">
                            <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-amber/70">
                              and
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 px-3 py-2.5">
                          {/* Field — grouped by section */}
                          <Select value={c.field} onValueChange={(v) => setConditionField(i, v)}>
                            <SelectTrigger className={`flex-1 ${FIELD_CLS}`}>
                              <SelectValue placeholder="Field" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                              {sch.sections.map((section) => {
                                const inSection = sch.fields.filter((f) => f.section === section);
                                if (!inSection.length) return null;
                                return (
                                  <SelectGroup key={section}>
                                    <SelectLabel className="text-amber/80">{section}</SelectLabel>
                                    {inSection.map((f) => (
                                      <SelectItem key={f.key} value={f.key}>
                                        {f.label}
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          {/* Operator — filtered to the field's type */}
                          <Select value={c.op} onValueChange={(v) => updateCondition(i, { op: v })}>
                            <SelectTrigger className={`w-[140px] ${FIELD_CLS}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {(sch.operators_by_type[typeOf(c.field)] ?? []).map((op) => (
                                <SelectItem key={op} value={op}>
                                  {sch.operators[op]?.label ?? op}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {/* Value — adapts to the field's type */}
                          {typeOf(c.field) === "number" ? (
                            <Input
                              type="number"
                              className={`w-28 ${FIELD_CLS}`}
                              value={c.value as number}
                              onChange={(e) => updateCondition(i, { value: Number(e.target.value) })}
                            />
                          ) : typeOf(c.field) === "boolean" ? (
                            <span className="w-28 text-center text-[11px] text-muted-foreground">
                              (no value)
                            </span>
                          ) : (
                            <Input
                              type="text"
                              className={`w-40 ${FIELD_CLS}`}
                              placeholder={c.op === "is_one_of" ? "a, b, c" : "value"}
                              value={String(c.value ?? "")}
                              onChange={(e) => updateCondition(i, { value: e.target.value })}
                            />
                          )}
                          <button
                            type="button"
                            className="px-1 text-muted-foreground transition-colors hover:text-destructive disabled:opacity-25"
                            disabled={draft.conditions.length <= 1}
                            onClick={() => removeCondition(i)}
                            aria-label="Remove condition"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addCondition}
                      className="w-full border-t border-[hsl(216,11%,24%)] py-2 text-xs font-medium text-amber transition-colors hover:bg-amber-dim"
                    >
                      + Add condition
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 space-y-1.5">
                    <label className={MICRO_LABEL}>NA reason <span className="text-muted-foreground/60 normal-case tracking-normal">(shown to underwriters)</span></label>
                    <Textarea
                      value={draft.na_reason}
                      onChange={(e) => setDraft({ ...draft, na_reason: e.target.value })}
                      placeholder="e.g. FICO must be >= 550"
                      className={`min-h-[64px] ${FIELD_CLS}`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className={MICRO_LABEL}>Priority</label>
                    <Input
                      type="number"
                      value={draft.sort_order}
                      onChange={(e) => setDraft({ ...draft, sort_order: Number(e.target.value) })}
                      className={FIELD_CLS}
                    />
                    <p className="text-[10px] text-muted-foreground">Lower runs first.</p>
                  </div>
                </div>
              </div>
            )}

            {/* footer */}
            <div
              className="flex shrink-0 justify-end gap-3 px-8 pb-7 pt-4"
              style={{ borderTop: `1px solid ${HAIRLINE}` }}
            >
              <Button
                variant="outline"
                className="border-[hsl(216,11%,30%)] bg-transparent text-foreground hover:bg-muted"
                disabled={saving}
                onClick={() => setFormOpen(false)}
              >
                Cancel
              </Button>
              <Button
                className="min-w-[130px] bg-amber font-medium text-background hover:bg-amber/90"
                disabled={saving}
                onClick={save}
              >
                {saving ? "Saving…" : editing ? "Save changes" : "Create guardrail"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Delete confirm ── */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!deleting && !v) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md gap-0 overflow-hidden border-0 p-0">
          <div style={{ background: "hsl(218, 30%, 8%)", color: "hsl(30, 9%, 95%)" }}>
            <div className="px-8 pb-4 pt-7" style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
              <div className={EYEBROW}>Delete guardrail</div>
              <DialogTitle className="mt-1 text-xl font-semibold text-foreground">
                {deleteTarget?.name}
              </DialogTitle>
              <DialogDescription className="mt-1 text-sm text-muted-foreground">
                This removes the rule from scoring. The change stays in the audit history, but the rule
                itself can't be restored.
              </DialogDescription>
            </div>
            <div className="flex justify-end gap-3 px-8 pb-7 pt-4">
              <Button
                variant="outline"
                className="border-[hsl(216,11%,30%)] bg-transparent text-foreground hover:bg-muted"
                disabled={deleting}
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </Button>
              <Button
                className="bg-destructive text-white hover:bg-destructive/90"
                disabled={deleting}
                onClick={confirmDelete}
              >
                {deleting ? "Deleting…" : "Delete guardrail"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── History ── */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl gap-0 overflow-hidden border-0 p-0">
          <div
            className="flex max-h-[82vh] flex-col"
            style={{ background: "hsl(218, 30%, 8%)", color: "hsl(30, 9%, 95%)" }}
          >
            <div className="shrink-0 px-8 pb-4 pt-7" style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
              <div className={EYEBROW}>Audit history</div>
              <DialogTitle className="mt-1 text-xl font-semibold text-foreground">{historyTitle}</DialogTitle>
              <DialogDescription className="mt-1 text-sm text-muted-foreground">
                Every change, most recent first.
              </DialogDescription>
            </div>
            <div className="flex-1 overflow-y-auto px-8 py-5">
              {history.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">No history yet.</p>
              ) : (
                <table className="w-full border-collapse text-xs">
                  <thead className="border-b border-border">
                    <tr>
                      <th className={TH}>When</th>
                      <th className={TH}>Action</th>
                      <th className={TH}>Rule</th>
                      <th className={TH}>By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h) => (
                      <tr key={h.history_id} className="border-b border-border/50 last:border-0">
                        <td className="px-4 py-2.5 tabular-nums text-muted-foreground">
                          {new Date(h.changed_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="border border-border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground/80">
                            {h.action}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-foreground/80">
                          {h.after?.name || h.before?.name || h.slug || `#${h.guardrail_id}`}
                        </td>
                        <td className="px-4 py-2.5 text-foreground/80">{h.changed_by}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
